import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import {
  Building2, Eye, EyeOff, Pencil, Check, X, ChevronRight, ChevronLeft,
  ShieldCheck, Layers, ScanLine, Coins, Scale, Image as ImageIcon, Upload, Trash2, Loader2, Headphones,
} from "lucide-react";

const gbp = (n) => "£" + (n || 0).toLocaleString("en-GB");
const bandDot = { red: "bg-red-500", amber: "bg-amber-500", green: "bg-emerald-500", na: "bg-gray-300" };
const bandText = { red: "text-red-600", amber: "text-amber-600", green: "text-emerald-600", na: "text-gray-400" };

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${active ? "border-ek-purple bg-ek-purple/10 text-ek-purple" : "border-black/10 text-ek-ink/60 hover:border-ek-purple/40"}`}>
      {children}
    </button>
  );
}

function Wizard({ options, initial, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState(() => ({
    company_name: initial.company_name || "",
    org_type: initial.org_type || "private", revenue_band: initial.revenue_band || "250m_1b",
    headcount: initial.headcount || 1000, risk_appetite: initial.risk_appetite || "balanced",
    sectors: initial.sectors || [], geographies: initial.geographies || [],
    business_model_flags: initial.business_model_flags || {},
  }));
  const toggleArr = (key, val) => setD((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));
  const toggleFlag = (key) => setD((s) => ({ ...s, business_model_flags: { ...s.business_model_flags, [key]: !s.business_model_flags[key] } }));

  const save = async () => {
    try { const { data } = await api.put("/org/profile", d); toast.success("Profile saved"); onSaved(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const steps = ["Organisation", "Footprint", "Business model", "Review"];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="org-wizard">
      <div className="w-full max-w-2xl rounded-md bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-ek-ink">Organisation profile</h3>
          <button onClick={onClose} data-testid="wizard-close"><X size={18} className="text-ek-ink/65" /></button>
        </div>
        <div className="mt-3 flex gap-1">
          {steps.map((s, i) => <div key={s} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-ek-purple" : "bg-black/10"}`} />)}
        </div>
        <p className="mt-3 ek-eyebrow">{steps[step]}</p>

        <div className="mt-3 min-h-[230px]">
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-ek-ink/65">Company name <span className="text-ek-ink/35">(shown on the exported board pack)</span></p>
                <input
                  type="text"
                  value={d.company_name}
                  onChange={(e) => setD({ ...d, company_name: e.target.value })}
                  placeholder="e.g. Acme Holdings plc"
                  maxLength={120}
                  data-testid="org-company-name"
                  className="mt-1.5 w-full rounded-md border border-black/10 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
                />
              </div>
              <div>
                <p className="text-xs text-ek-ink/65">Organisation type</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {options.org_types.map((o) => <Chip key={o.key} active={d.org_type === o.key} onClick={() => setD({ ...d, org_type: o.key })} testid={`type-${o.key}`}>{o.label}</Chip>)}
                </div>
              </div>
              <div>
                <p className="text-xs text-ek-ink/65">Annual revenue</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {options.revenue_bands.map((o) => <Chip key={o.key} active={d.revenue_band === o.key} onClick={() => setD({ ...d, revenue_band: o.key })} testid={`rev-${o.key}`}>{o.label}</Chip>)}
                </div>
              </div>
              <div>
                <p className="text-xs text-ek-ink/65">Risk appetite</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {options.risk_appetites.map((o) => <Chip key={o.key} active={d.risk_appetite === o.key} onClick={() => setD({ ...d, risk_appetite: o.key })} testid={`appetite-${o.key}`}>{o.label}</Chip>)}
                </div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-ek-ink/65">Sector(s)</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {options.sectors.map((o) => <Chip key={o.key} active={d.sectors.includes(o.key)} onClick={() => toggleArr("sectors", o.key)} testid={`sector-${o.key}`}>{o.label}</Chip>)}
                </div>
              </div>
              <div>
                <p className="text-xs text-ek-ink/65">Operating geographies</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {options.geographies.map((g) => <Chip key={g} active={d.geographies.includes(g)} onClick={() => toggleArr("geographies", g)} testid={`geo-${g}`}>{g}</Chip>)}
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <p className="text-xs text-ek-ink/65">Tick everything that applies — this filters which risks the platform shows you.</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {options.business_flags.map((f) => (
                  <button key={f.key} onClick={() => toggleFlag(f.key)} data-testid={`flag-${f.key}`}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-xs ${d.business_model_flags[f.key] ? "border-ek-purple bg-ek-purple/5" : "border-black/10"}`}>
                    <span className={`grid h-4 w-4 place-items-center rounded ${d.business_model_flags[f.key] ? "bg-ek-purple text-white" : "border border-black/20"}`}>
                      {d.business_model_flags[f.key] && <Check size={11} />}
                    </span>
                    <span className="text-ek-ink/70">{f.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-2 text-sm text-ek-ink/70" data-testid="wizard-review">
              <p><b>{d.company_name || "—"}</b></p>
              <p><b>{options.org_types.find((o) => o.key === d.org_type)?.label}</b> · {options.revenue_bands.find((o) => o.key === d.revenue_band)?.label} · {d.headcount.toLocaleString()} staff · {d.risk_appetite} appetite</p>
              <p>Sectors: {d.sectors.join(", ") || "—"}</p>
              <p>Geographies: {d.geographies.join(", ") || "—"}</p>
              <p>Flags: {Object.entries(d.business_model_flags).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"}</p>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-between">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="ek-btn-outline disabled:opacity-40" data-testid="wizard-back"><ChevronLeft size={15} /> Back</button>
          {step < 3
            ? <button onClick={() => setStep((s) => s + 1)} className="ek-btn" data-testid="wizard-next">Next <ChevronRight size={15} /></button>
            : <button onClick={save} className="ek-btn" data-testid="wizard-save"><Check size={15} /> Save profile</button>}
        </div>
      </div>
    </div>
  );
}

function BrandLogo({ canWrite }) {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/org/logo").then(({ data }) => setLogo(data.logo || null)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Logo must be 512 KB or smaller."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("logo", file);
      const { data } = await api.post("/org/logo", form);
      setLogo(data.logo);
      toast.success("Logo uploaded — it will appear on your board pack cover.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete("/org/logo"); setLogo(null); toast.success("Logo removed."); }
    catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-md border border-black/5 bg-white p-5" data-testid="brand-logo-card">
      <p className="ek-eyebrow flex items-center gap-1"><ImageIcon size={12} /> Board-pack logo</p>
      <div className="mt-3 flex flex-wrap items-center gap-5">
        <div className="grid h-20 w-44 place-items-center rounded-md border border-dashed border-black/15 bg-ek-canvas p-2" data-testid="brand-logo-preview">
          {logo
            ? <img src={logo} alt="Brand logo" className="max-h-16 max-w-full object-contain" />
            : <span className="text-xs text-ek-ink/35">No logo set</span>}
        </div>
        <div className="flex-1">
          <p className="text-sm text-ek-ink/60">
            Upload your company logo to replace the Ekselens star on the exported board-pack cover.
            PNG, JPEG or WEBP · up to 512&nbsp;KB.
          </p>
          {canWrite && (
            <div className="mt-3 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPick} className="hidden" data-testid="brand-logo-input" />
              <button onClick={() => fileRef.current?.click()} disabled={busy} className="ek-btn flex items-center gap-1.5" data-testid="brand-logo-upload">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {logo ? "Replace logo" : "Upload logo"}
              </button>
              {logo && (
                <button onClick={remove} disabled={busy} className="ek-btn-outline flex items-center gap-1.5 text-red-500" data-testid="brand-logo-remove">
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function VoiceBriefingPrefs({ canWrite }) {
  // iter135 — admin/CRO toggle for the nightly board-briefing auto-warm.
  // Default OFF (manual only) to cap ElevenLabs spend during pilots.
  const [autowarm, setAutowarm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/org/voice-prefs")
       .then(({ data }) => setAutowarm(!!data.autowarm_briefing))
       .catch(() => setAutowarm(false));
  }, []);
  const toggle = async () => {
    if (busy || !canWrite || autowarm === null) return;
    const next = !autowarm;
    setBusy(true);
    try {
      const { data } = await api.put("/org/voice-prefs", { autowarm_briefing: next });
      setAutowarm(!!data.autowarm_briefing);
      toast.success(next
        ? "Daily auto-refresh ON — briefing will regenerate overnight."
        : "Daily auto-refresh OFF — refresh manually from the dashboard.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update voice preferences.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 rounded-md border border-black/5 bg-white p-5" data-testid="org-voice-prefs">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="ek-eyebrow flex items-center gap-1"><Headphones size={12} /> Board briefing automation</h3>
          <p className="mt-1 font-display text-lg font-semibold text-ek-ink">Daily 60-second briefing</p>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-ek-ink/70">
            Each refresh runs one ElevenLabs synth pass for your tenant. Leave this
            <span className="font-medium"> OFF</span> to control voice spend during a pilot — your admin
            or CRO can click <span className="font-mono text-[12px]">Refresh today&apos;s briefing</span> on
            the dashboard whenever they want a fresh version. Switch it
            <span className="font-medium"> ON</span> to have the briefing pre-generated nightly at 05:30 UTC.
          </p>
        </div>
        <button
          onClick={toggle}
          disabled={!canWrite || busy || autowarm === null}
          data-testid="voice-autowarm-toggle"
          aria-pressed={!!autowarm}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 ${
            autowarm ? "border-emerald-300 bg-emerald-500" : "border-black/10 bg-gray-200"
          }`}
          title={canWrite ? "Toggle nightly auto-refresh" : "Only admin or CRO can change this"}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${autowarm ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-gray-50 px-2.5 py-1 text-xs text-ek-ink/70">
        <span className={`h-1.5 w-1.5 rounded-full ${autowarm ? "bg-emerald-500" : "bg-gray-400"}`} />
        {autowarm === null ? "Loading…" : autowarm ? "Auto-refresh ON" : "Manual refresh only"}
      </div>
    </div>
  );
}


export default function Organisation() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "org.admin");
  const [profile, setProfile] = useState(null);
  const [materiality, setMateriality] = useState(null);
  const [domains, setDomains] = useState(null);
  const [report, setReport] = useState(null);
  const [options, setOptions] = useState(null);
  const [focus, setFocus] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    api.get("/org/profile").then(({ data }) => { setProfile(data.profile); setMateriality(data.materiality); }).catch(() => toast.error("Could not load profile"));
    api.get("/org/domains").then(({ data }) => setDomains(data)).catch(() => {});
    api.get("/org/applicability").then(({ data }) => setReport(data)).catch(() => {});
    api.get("/org/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleDomains = (cat) => (domains?.items || []).filter((d) => d.category === cat && (!focus || d.applicable));

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Organisation profile" description="Profile-driven applicability & materiality." path="/organisation" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="organisation-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="ek-eyebrow">Relevance &amp; materiality</h1>
            <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Organisation profile</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
              Your profile drives what&apos;s relevant — factors and risk domains that don&apos;t apply to your business model
              are marked <em>Not applicable</em> (with a rationale) and excluded from scoring. Thresholds scale to your size.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setFocus((v) => !v)} className="ek-btn-outline flex items-center gap-1.5" data-testid="focus-toggle">
              {focus ? <EyeOff size={15} /> : <Eye size={15} />} {focus ? "Show N/A" : "Focus mode"}
            </button>
            {canWrite && <button onClick={() => setEditing(true)} className="ek-btn flex items-center gap-1.5" data-testid="edit-profile-button"><Pencil size={15} /> Edit profile</button>}
          </div>
        </div>

        {/* Profile + materiality */}
        {profile && materiality && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="profile-summary">
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow flex items-center gap-1"><Building2 size={12} /> Organisation</p>
              {profile.company_name && <p className="mt-1 font-display text-lg font-semibold text-ek-ink" data-testid="org-company-display">{profile.company_name}</p>}
              <p className="mt-1 text-sm font-medium text-ek-ink/70 capitalize">{(options?.org_types || []).find((o) => o.key === profile.org_type)?.label || profile.org_type}</p>
              <p className="mt-1 text-xs text-ek-ink/65">{(profile.sectors || []).join(", ")} · {(profile.geographies || []).length} geographies · {profile.headcount?.toLocaleString()} staff</p>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow flex items-center gap-1"><Coins size={12} /> Materiality</p>
              <p className="mt-1 font-display text-2xl font-bold text-ek-ink">{gbp(materiality.material_threshold_gbp)}</p>
              <p className="mt-1 text-xs text-ek-ink/65">material · severe {gbp(materiality.severe_threshold_gbp)} · {profile.risk_appetite} appetite</p>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow flex items-center gap-1"><ScanLine size={12} /> Applicability</p>
              <p className="mt-1 font-display text-2xl font-bold text-ek-ink">{report?.applicable_domains ?? "—"} / {(report?.applicable_domains || 0) + (report?.na_domains || 0)}</p>
              <p className="mt-1 text-xs text-ek-ink/65">risk domains applicable · {report?.na_domains ?? 0} N/A</p>
            </div>
          </div>
        )}

        {/* White-label board-pack logo */}
        <BrandLogo canWrite={canWrite} />

        {/* iter135 — Voice briefing automation (admin/CRO) */}
        <VoiceBriefingPrefs canWrite={canWrite} />

        {/* Profile-weighted internal vs external calibration */}
        {domains?.calibration && (
          <div className="mt-10 rounded-md border border-ek-purple/20 bg-gradient-to-br from-ek-purple/[0.06] to-transparent p-6" data-testid="calibration-panel">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h3 className="flex items-center gap-2 font-display text-xl font-bold text-ek-ink">
                  <Scale size={18} className="text-ek-purple" /> Profile-weighted calibration
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-ek-ink/65">
                  Your business model tilts the balance of internal vs external exposure — these weights
                  are applied to the applicable domain scores to produce a single calibrated index.
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-ek-ink/65">Calibrated index</p>
                <p className={`font-display text-4xl font-bold ${bandText[domains.calibration.band]}`} data-testid="calibration-index">
                  {domains.calibration.calibrated_index}
                </p>
                <p className="text-xs text-ek-ink/65">leans <strong className="capitalize">{domains.calibration.lean}</strong></p>
              </div>
            </div>

            {/* Weight bar */}
            <div className="mt-5">
              <div className="flex h-8 overflow-hidden rounded-md" data-testid="calibration-weight-bar">
                <div className="flex items-center justify-start bg-ek-ink px-3 text-xs font-bold text-white"
                  style={{ width: `${domains.calibration.internal_weight * 100}%` }}>
                  Internal {Math.round(domains.calibration.internal_weight * 100)}%
                </div>
                <div className="flex items-center justify-end bg-ek-purple px-3 text-xs font-bold text-white"
                  style={{ width: `${domains.calibration.external_weight * 100}%` }}>
                  External {Math.round(domains.calibration.external_weight * 100)}%
                </div>
              </div>
              <div className="mt-2 flex justify-between text-xs text-ek-ink/65">
                <span>Internal exposure score: <strong className={bandText[domains.calibration.internal_score >= 60 ? "red" : domains.calibration.internal_score >= 30 ? "amber" : "green"]}>{domains.calibration.internal_score ?? "—"}</strong></span>
                <span>External exposure score: <strong className={bandText[domains.calibration.external_score >= 60 ? "red" : domains.calibration.external_score >= 30 ? "amber" : "green"]}>{domains.calibration.external_score ?? "—"}</strong></span>
              </div>
            </div>

            {/* Drivers */}
            <div className="mt-5">
              <p className="ek-eyebrow">What's tilting the balance</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="calibration-drivers">
                {domains.calibration.drivers.map((d, i) => (
                  <span key={i}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${d.side === "external" ? "bg-ek-purple/10 text-ek-purple" : "bg-ek-ink/5 text-ek-ink/70"}`}>
                    {d.factor} <span className="opacity-60">+{Math.round(d.weight * 100)}% {d.side}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Risk domains */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {["external", "internal"].map((cat) => (
            <div key={cat} data-testid={`domains-${cat}`}>
              <h3 className="ek-eyebrow flex items-center gap-1"><Layers size={12} /> {cat} risk domains</h3>
              <div className="mt-3 space-y-3">
                {visibleDomains(cat).map((d) => (
                  <div key={d.key} className={`rounded-md border border-black/5 bg-white p-4 ${!d.applicable ? "opacity-60" : ""}`} data-testid={`domain-${d.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${bandDot[d.band]}`} />
                        <h4 className="font-display text-sm font-semibold text-ek-ink">{d.name}</h4>
                      </div>
                      {d.applicable
                        ? <span className={`font-display text-xl font-bold ${bandText[d.band]}`}>{d.domain_score}</span>
                        : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-500">N/A</span>}
                    </div>
                    {!d.applicable && <p className="mt-1 text-xs text-ek-ink/65">{d.rationale}</p>}
                    {d.applicable && (
                      <div className="mt-2 space-y-1">
                        {d.indicators.filter((i) => !focus || i.applicable).map((i) => (
                          <div key={i.key} className="flex items-center justify-between text-xs">
                            <span className={`flex items-center gap-1.5 ${i.applicable ? "text-ek-ink/60" : "text-gray-400"}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${bandDot[i.band]}`} /> {i.name}
                            </span>
                            <span className={i.applicable ? "font-medium text-ek-ink/70" : "text-gray-400"}>
                              {i.applicable ? `${i.value}${i.unit === "%" ? "%" : i.unit === "count" ? "" : " " + i.unit}` : "N/A"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Exogenous applicability report */}
        {report && (
          <div className="mt-10 rounded-md border border-black/5 bg-white p-5" data-testid="applicability-report">
            <h3 className="ek-eyebrow flex items-center gap-1"><ShieldCheck size={12} /> External-factor applicability</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {report.exogenous_factors.filter((f) => !focus || f.applicable).map((f) => (
                <div key={f.key} className="flex items-start gap-2 rounded-md border border-black/5 bg-ek-canvas p-2.5 text-xs" data-testid={`exo-${f.key}`}>
                  <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full ${f.applicable ? "bg-emerald-500" : "bg-gray-300"} text-white`}>
                    {f.applicable ? <Check size={11} /> : <X size={11} />}
                  </span>
                  <div>
                    <p className="font-medium capitalize text-ek-ink">{f.key.replace(/_/g, " ")}</p>
                    <p className="text-ek-ink/65">{f.rationale}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {editing && options && profile && (
        <Wizard options={options} initial={profile}
          onClose={() => setEditing(false)}
          onSaved={(data) => { setProfile(data.profile); setMateriality(data.materiality); setEditing(false); load(); }} />
      )}
    </div>
  );
}
